import React, { useMemo, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { FixedSizeList as VirtualList } from "react-window";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import SkillRow from "./SkillCard";
import theme from "../../../components/LayoutComponents/theme";

const TableHeader = ({ isMobile, sortBy, sortDirection, onSortToggle }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr auto auto" : "1fr 200px 56px",
      alignItems: "center",
      gap: 2,
      padding: "14px 16px",
      backgroundColor: theme.colors.purple,
      position: "sticky",
      top: 0,
      zIndex: 1,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "pointer",
        userSelect: "none",
        "&:hover": {
          opacity: 0.9,
        },
      }}
      onClick={() => onSortToggle('name')}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.875rem",
          color: "#fff",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        Skill Name
      </Typography>
      <IconButton
        size="small"
        sx={{
          color: "#fff",
          padding: 0,
          opacity: sortBy === 'name' ? 1 : 0.5,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        {sortBy === 'name' ? (
          sortDirection === "asc" ? (
            <ArrowUpwardIcon fontSize="small" />
          ) : (
            <ArrowDownwardIcon fontSize="small" />
          )
        ) : (
          <UnfoldMoreIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "pointer",
        userSelect: "none",
        justifyContent: isMobile ? "center" : "flex-start",
        "&:hover": {
          opacity: 0.9,
        },
      }}
      onClick={() => onSortToggle('proficiency')}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.875rem",
          color: "#fff",
          textTransform: "uppercase",
          letterSpacing: "0.5px",
        }}
      >
        Proficiency
      </Typography>
      <IconButton
        size="small"
        sx={{
          color: "#fff",
          padding: 0,
          opacity: sortBy === 'proficiency' ? 1 : 0.5,
          "&:hover": {
            backgroundColor: "transparent",
          },
        }}
      >
        {sortBy === 'proficiency' ? (
          sortDirection === "asc" ? (
            <ArrowUpwardIcon fontSize="small" />
          ) : (
            <ArrowDownwardIcon fontSize="small" />
          )
        ) : (
          <UnfoldMoreIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
    <Typography
      sx={{
        fontWeight: 600,
        fontSize: "0.875rem",
        color: "#fff",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        textAlign: "center",
      }}
    >
      {/* Actions column - no header text for cleaner look */}
    </Typography>
  </Box>
);

const Row = ({ index, style, data }) => {
  const { skills, ...rest } = data;
  const skill = skills[index];
  
  return (
    <div style={style}>
      <SkillRow skill={skill} {...rest} />
    </div>
  );
};

export default function SkillsList({
  session,
  skills,
  setSkills,
  proficiencies,
  handleProficiencyChange,
  newlyAddedSkills,
  isMobile,
  loading,
  setLoading,
  sortBy,
  setSortBy,
  sortDirection,
  setSortDirection,
  showToast,
}) {
  useEffect(() => {
    if (skills?.length > 0) {
      setLoading(false);
    } else {
      setLoading(true);
    }
  }, [skills, setLoading]);

  const filteredSkills = useMemo(
    () => skills?.filter((s) => s.competencyId),
    [skills]
  );

  const sortedSkills = useMemo(() => {
    if (!filteredSkills || !sortBy) return filteredSkills || [];
    
    const sorted = [...filteredSkills].sort((a, b) => {
      if (sortBy === 'name') {
        const nameA = (a.competencyDescription || "").toLowerCase();
        const nameB = (b.competencyDescription || "").toLowerCase();
        if (sortDirection === "asc") {
          return nameA.localeCompare(nameB);
        } else {
          return nameB.localeCompare(nameA);
        }
      } else if (sortBy === 'proficiency') {
        const profA = a.proficiencyRating ?? 0;
        const profB = b.proficiencyRating ?? 0;
        if (sortDirection === "asc") {
          return profA - profB; // Lower to higher
        } else {
          return profB - profA; // Higher to lower
        }
      }
      return 0;
    });
    return sorted;
  }, [filteredSkills, sortBy, sortDirection]);

  const handleSortToggle = (column) => {
    if (sortBy === column) {
      // Same column - toggle direction
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      // Different column - set new sort column and default to asc
      setSortBy(column);
      setSortDirection("asc");
    }
  };

  const ROW_HEIGHT = 56;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 400,
        }}
      >
        <LoadingCircle />
      </Box>
    );
  }

  if (!sortedSkills?.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 200,
          color: "#666",
        }}
      >
        <Typography>No skills found.</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderTop: "none",
        borderRadius: "0 0 8px 8px",
        overflow: "hidden",
        backgroundColor: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <TableHeader isMobile={isMobile} sortBy={sortBy} sortDirection={sortDirection} onSortToggle={handleSortToggle} />
      <VirtualList
        height={500}
        width="100%"
        itemCount={sortedSkills.length}
        itemSize={ROW_HEIGHT}
        itemData={{
          skills: sortedSkills,
          session,
          setSkills,
          proficiencies,
          handleProficiencyChange,
          newlyAddedSkills,
          setLoading,
          isMobile,
          showToast,
        }}
        overscanCount={5}
      >
        {Row}
      </VirtualList>
    </Box>
  );
}